import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import { MdArrowBack, MdDelete, MdDownload } from "react-icons/md";
import { FaUpload } from "react-icons/fa";

import Hoc from "../../../components/HOC";
import Loader from "../../../components/Loader";
import SimpleTable from "../../../components/SimpleTable";
import CustomToolTip from "../../../components/ToolTip";

import CandidateDocumentModal from "./CandidateDocumentModal";

import {
  deleteCandidateDocument,
  downloadCandidateDocument,
  getCandidateDocuments,
  uploadCandidateDocument,
} from "../../../Services/candidateDocuments";

const documentsList = [
  { label: "Passport", value: "PASSPORT" },
  { label: "Visa", value: "VISA" },
  { label: "Driving Licence", value: "DRIVING_LICENCE" },
  { label: "SSN", value: "SSN" },
  { label: "Resume", value: "RESUME" },
  { label: "Other", value: "OTHER" },
];

const initialDocumentValues = {
  documentType: "",
  file: null,
  issueDate: "",
  expiryDate: "",
  others: "",
};

const CandidateDocuments = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [candidateName, setCandidateName] = useState("");

  const candidateId = useMemo(() => Number(id), [id]);

  const fetchDocuments = useCallback(async () => {
    if (!candidateId || candidateId === 0) {
      return;
    }

    setIsLoading(true);
    try {
      const data = await getCandidateDocuments(candidateId);
      const filteredDocuments = data.filter((doc) => Boolean(doc.fileName));
      setDocuments([...filteredDocuments].reverse());
      
      // Extract candidateName from API response (present in each document)
      if (data && Array.isArray(data) && data.length > 0) {
        const name = data[0].candidateName || data.find(doc => doc.candidateName)?.candidateName;
        if (name) {
          setCandidateName(name);
        }
      }
    } catch (error) {
      setDocuments([]);
      toast.error(error?.message || "Failed to load candidate documents");
    } finally {
      setIsLoading(false);
    }
  }, [candidateId]);

    

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUploadDocument = async (values) => {
    if (!candidateId || candidateId === 0) {
      toast.error("Candidate information missing.");
      return;
    }

    const documentType =
      values.documentType === "OTHER"
        ? values.others?.trim().toUpperCase()
        : values.documentType;

    try {
      setIsLoading(true);
      await uploadCandidateDocument(
        candidateId,
        documentType,
        values.issueDate,
        values.expiryDate,
        values.file
      );
      toast.success("Document uploaded successfully");
      setShowModal(false);
      fetchDocuments();
    } catch (error) {
      toast.error(error?.message || "Failed to upload document");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDocument = async (row) => {
    if (!candidateId || candidateId === 0) {
      toast.error("Candidate information missing.");
      return;
    }

    try {
      setIsLoading(true);
      await deleteCandidateDocument(candidateId, row.documentType);
      toast.success("Document deleted successfully");
      fetchDocuments();
    } catch (error) {
      toast.error(error?.message || "Failed to delete document");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadDocument = async (row) => {
    try {
      let base64Data = row.downloadLink;
      let filename = row.originalFilename || row.documentType || "document";

      if (!base64Data) {
        const downloadResponse = await downloadCandidateDocument(row.fileName);
        base64Data = downloadResponse?.base64Data || "";
        if (downloadResponse?.originalFilename) {
          filename = downloadResponse.originalFilename;
        }
      }

      if (!base64Data) {
        toast.error("Download link not available");
        return;
      }

      const link = window.document.createElement("a");
      link.href = base64Data;
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      toast.success(`Downloading ${filename}`);
    } catch (error) {
      toast.error(error?.message || "Failed to download document");
    }
  };


  const columns = [
    {
      name: "Document Type",
      selector: "documentType",
      renderCell: (row) => row.documentType || "N/A",
    },
    {
      name: "Issue Date",
      selector: "issueDate",
      renderCell: (row) =>
        row.issueDate
          ? moment(row.issueDate).format("MM-DD-YYYY")
          : "Not Applicable",
    },
    {
      name: "Expiry Date",
      selector: "expiryDate",
      renderCell: (row) =>
        row.expiryDate
          ? moment(row.expiryDate).format("MM-DD-YYYY")
          : "Not Applicable",
    },
    {
      name: "Actions",
      selector: "actions",
      renderCell: (row) => (
        <div className="flex items-center justify-center space-x-4">
          <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteDocument(row);
              }}
            className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-colors"
          >
            <>
              <MdDelete size={20} data-tip data-for={`Delete-${row.id}`} />
              <CustomToolTip id={`Delete-${row.id}`} title="Delete" />
            </>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadDocument(row);
            }}
            className="text-primary hover:text-blue-700 p-2 rounded-full hover:bg-blue-50 transition-colors"
          >
            <>
              <MdDownload size={20} data-tip data-for={`Download-${row.id}`} />
              <CustomToolTip id={`Download-${row.id}`} title="Download" />
            </>
          </button>
        </div>
      ),
      headerClassName: "text-center",
    },
  ];

  if (!candidateId || candidateId === 0) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Invalid candidate ID</p>
          <button
            onClick={() => navigate("/candidates")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Candidates
          </button>
        </div>
      </div>
    );
  }
  

  return (
    <div className="bg-gray-50 min-h-screen">
      <Loader isLoading={isLoading} />

      <div className="p-6 max-w-7xl mx-auto">
        <button
          type="button"
          className="mb-4 inline-flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
          onClick={() => navigate("/candidates")}
        >
          <MdArrowBack className="mr-2" size={20} /> Back to Candidates
        </button>

        <div className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4">
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center transition-colors self-start sm:self-auto"
            >
              <FaUpload className="mr-2" size={16} />
              Upload Document
            </button>
          </div>

          <div className="p-4">
            {documents.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-2">
                  <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-sm text-gray-500 mb-1">No documents found</p>
                <p className="text-xs text-gray-400">Upload a document to get started</p>
              </div>
            ) : (
              <SimpleTable columns={columns} data={documents} scrollHeight="60vh" />
            )}
          </div>
        </div>
      </div>

      <CandidateDocumentModal
        show={showModal}
        onClose={() => setShowModal(false)}
        title="Upload Candidate Document"
        initialValues={initialDocumentValues}
        documentsList={documentsList}
        submitForm={handleUploadDocument}
      />
    </div>
  );
};

export default Hoc(CandidateDocuments);
